// Anteprima rapportino — genera PDF e mostra in iframe fullscreen
import { useState, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import { Download, FileText, Loader2, X } from 'lucide-react'
import api from '../../lib/api'
import type { Report } from '@fieldcore/types'

export function ReportPreviewPage() {
  const { id } = useParams<{ id: string }>()
  // blobUrl: URL locale creato da Blob — non espone URL MinIO/API al browser
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [pdfError, setPdfError] = useState(false)
  const [fetchingBlob, setFetchingBlob] = useState(false)
  const blobRef = useRef<string | null>(null)

  // Cleanup blob URL quando il componente viene smontato
  useEffect(() => {
    return () => {
      if (blobRef.current) URL.revokeObjectURL(blobRef.current)
    }
  }, [])

  // Carica dati rapportino
  const { data: report, isLoading } = useQuery({
    queryKey: ['report', id],
    queryFn: async () => {
      const res = await api.get<Report>(`/reports/${id}`)
      return res.data
    },
    enabled: Boolean(id),
  })

  // Genera PDF, poi scarica il binario con auth header e crea Blob URL locale
  const generatePdf = useMutation({
    mutationFn: async () => {
      // 1. Chiede al backend di generare/verificare il PDF
      const res = await api.post<{ pdfUrl: string }>(`/reports/${id}/pdf`)
      // Strip /api prefix: api instance has baseURL ending with /api già
      const apiUrl = res.data.pdfUrl.replace(/^\/api/, '')

      // 2. Scarica il PDF con il token JWT incluso (axios lo aggiunge automaticamente)
      setFetchingBlob(true)
      const blob = await api.get<Blob>(apiUrl, { responseType: 'blob' })
      return blob.data
    },
    onSuccess: (blob) => {
      if (blobRef.current) URL.revokeObjectURL(blobRef.current)
      const url = URL.createObjectURL(blob)
      blobRef.current = url
      setBlobUrl(url)
      setFetchingBlob(false)
    },
    onError: () => {
      setFetchingBlob(false)
      setPdfError(true)
    },
  })

  // Auto-genera appena il report è caricato
  const hasTriggered = generatePdf.isPending || generatePdf.isSuccess || generatePdf.isError
  if (report && !hasTriggered) {
    generatePdf.mutate()
  }

  const STATUS_IT: Record<string, string> = {
    draft: 'Bozza', completed: 'Completato', signed: 'Firmato', sent: 'Inviato',
  }
  const TYPE_IT: Record<string, string> = {
    fault: 'Guasto', scheduled_maintenance: 'Manutenzione programmata',
    inspection: 'Ispezione', installation: 'Installazione',
    testing: 'Collaudo', urgent: 'Urgente',
  }

  return (
    <div className="flex flex-col h-screen bg-gray-100">
      {/* Barra superiore */}
      <div className="flex items-center justify-between bg-white border-b border-gray-200 px-4 py-2 shadow-sm shrink-0">
        <div className="flex items-center gap-3">
          <FileText className="h-5 w-5 text-blue-600" />
          <div>
            <p className="text-sm font-semibold text-gray-900">
              {report ? `Rapportino ${report.reportNumber}` : 'Caricamento...'}
            </p>
            {report && (
              <p className="text-xs text-gray-500">
                {TYPE_IT[report.type] ?? report.type} · {STATUS_IT[report.status] ?? report.status}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {blobUrl && (
            <a
              href={blobUrl}
              download={`rapportino-${report?.reportNumber ?? id}.pdf`}
              className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
            >
              <Download className="h-4 w-4" />
              Scarica PDF
            </a>
          )}
          <button
            onClick={() => window.close()}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors"
            title="Chiudi"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Contenuto principale */}
      {(isLoading || generatePdf.isPending || fetchingBlob) ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 text-gray-500">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          <p className="text-sm">{isLoading ? 'Caricamento rapportino...' : 'Generazione PDF in corso...'}</p>
        </div>
      ) : pdfError ? (
        /* Fallback HTML — mostra dati rapportino senza PDF */
        <div className="flex-1 overflow-auto p-8">
          <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-lg overflow-hidden">
            {/* Header blu stile Kleis */}
            <div className="bg-[#1e3a5f] text-white px-8 py-6">
              <div className="flex justify-between items-start">
                <div>
                  <h1 className="text-xl font-bold tracking-wide">RAPPORTO DI ASSISTENZA</h1>
                  <p className="text-blue-200 text-sm mt-0.5">HARDWARE – SOFTWARE</p>
                </div>
                <div className="text-right text-sm text-blue-200">
                  <p className="font-semibold text-white">{report?.reportNumber}</p>
                  <p>{report?.createdAt ? new Date(report.createdAt).toLocaleDateString('it-IT') : ''}</p>
                </div>
              </div>
            </div>

            <div className="px-8 py-6 space-y-6">
              {/* Dati intervento */}
              <section>
                <h2 className="text-xs font-bold uppercase tracking-widest text-gray-400 border-b border-gray-200 pb-1 mb-3">Dettaglio Intervento</h2>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <Row label="Tipo" value={TYPE_IT[report?.type ?? ''] ?? report?.type ?? '—'} />
                  <Row label="Stato" value={STATUS_IT[report?.status ?? ''] ?? report?.status ?? '—'} />
                  <Row label="Ore lavorate" value={report?.totalHours ? `${report.totalHours} h` : '—'} />
                  <Row label="Km percorsi" value={report?.travelKm ? `${report.travelKm} km` : '—'} />
                </div>
              </section>

              {/* Descrizione lavoro */}
              {report?.workDescription && (
                <section>
                  <h2 className="text-xs font-bold uppercase tracking-widest text-gray-400 border-b border-gray-200 pb-1 mb-3">Descrizione Lavoro</h2>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{report.workDescription}</p>
                </section>
              )}

              {/* Materiali */}
              {report?.materialsUsed && report.materialsUsed.length > 0 && (
                <section>
                  <h2 className="text-xs font-bold uppercase tracking-widest text-gray-400 border-b border-gray-200 pb-1 mb-3">Materiali Utilizzati</h2>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs font-semibold text-gray-500 uppercase">
                        <th className="text-left py-1 pr-4">Descrizione</th>
                        <th className="text-left py-1 pr-4">Codice</th>
                        <th className="text-right py-1 pr-4">Qtà</th>
                        <th className="text-right py-1">Importo</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {report.materialsUsed.map((mat, i) => (
                        <tr key={i}>
                          <td className="py-1.5 pr-4 text-gray-800">{mat.materialName}</td>
                          <td className="py-1.5 pr-4 font-mono text-xs text-gray-500">{mat.materialCode}</td>
                          <td className="py-1.5 pr-4 text-right">{mat.quantity}</td>
                          <td className="py-1.5 text-right text-gray-700">
                            {mat.unitCost ? `€ ${(mat.quantity * mat.unitCost).toFixed(2)}` : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>
              )}

              {/* Firma */}
              {report?.customerSignatureUrl && (
                <section>
                  <h2 className="text-xs font-bold uppercase tracking-widest text-gray-400 border-b border-gray-200 pb-1 mb-3">Firma Cliente</h2>
                  <img
                    src={report.customerSignatureUrl}
                    alt="Firma cliente"
                    className="max-h-24 rounded border border-gray-200 bg-white"
                  />
                  {report.customerSignerName && (
                    <p className="mt-1 text-sm text-gray-600">{report.customerSignerName}</p>
                  )}
                </section>
              )}

              <p className="text-center text-xs text-red-500">
                PDF non generato — mostrando anteprima HTML. Verificare configurazione S3.
              </p>
            </div>
          </div>
        </div>
      ) : blobUrl ? (
        <iframe
          src={blobUrl}
          title="Anteprima PDF rapportino"
          className="flex-1 w-full border-0"
          aria-label="Anteprima PDF rapportino"
        />
      ) : null}
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-gray-400 font-medium">{label}</p>
      <p className="text-gray-800 font-medium">{value}</p>
    </div>
  )
}
